<template>
  <b-modal
    :active="true"
    :can-cancel="['escape', 'x', 'outside']"
    @close="onClose"
  >
    <div class="content changelog-content">
      <header class="changelog-header">
        <b-icon icon="history" />
        <h2 class="changelog-title">{{ $t('change_log') }}</h2>
      </header>

      <div class="changelog-list" ref="list">
        <template v-if="entries.length">
          <article
            v-for="entry in entries"
            :key="entry._id"
            class="changelog-entry"
          >
            <div class="entry-header">
              <b-icon icon="account-circle" size="is-small" />
              <span class="entry-user">{{ userName(entry) }}</span>
              <template v-for="(part, partIndex) in phraseParts(entry)">
                <a
                  v-if="part.href"
                  :key="`${entry._id}-part-${partIndex}`"
                  class="entry-entity-link"
                  :href="part.href"
                  :target="part.inPlace ? undefined : '_blank'"
                  rel="noopener noreferrer"
                  @click="onLinkClick($event, part)"
                >
                  {{ part.text }}
                  <b-icon
                    v-if="!part.inPlace"
                    icon="open-in-new"
                    size="is-small"
                    class="entry-link-icon"
                  />
                </a>
                <span
                  v-else
                  :key="`${entry._id}-part-${partIndex}`"
                  :class="part.emphasis ? 'entry-entity-type' : 'entry-action'"
                >
                  {{ part.text }}
                </span>
              </template>
              <span v-if="entry.tool" class="entry-tool">
                {{ toolLabel(entry.tool) }}
              </span>
              <span class="entry-date">{{ formatDate(entry.at) }}</span>
            </div>

            <div v-if="headerText(entry)" class="entry-context">
              <span class="entry-header-part">{{ headerText(entry) }}</span>
            </div>

            <div
              v-for="(change, index) in visibleChanges(entry)"
              :key="`${entry._id}-${index}`"
              class="entry-change"
            >
              <span class="change-field">{{ fieldLabel(change.field) }}</span>
              <span class="change-original">
                <template
                  v-if="!change.contentChanged && hasValue(change.oldValue)"
                >
                  <strong>{{ $t('change_log_modal.original') }}:</strong>
                  {{ displayValue(change.oldValue, change.field) }}
                </template>
              </span>
              <span class="change-new">
                <template v-if="change.contentChanged">
                  {{ $t('change_log_modal.updated') }}
                </template>
                <template v-else>
                  <strong>{{ $t('change_log_modal.new') }}:</strong>
                  {{
                    hasValue(change.newValue)
                      ? displayValue(change.newValue, change.field)
                      : '—'
                  }}
                </template>
              </span>
            </div>
          </article>
        </template>

        <div v-else-if="!isLoading" class="changelog-empty">
          <b-icon icon="history" size="is-large" />
          <p>{{ $t('change_log_modal.empty') }}</p>
        </div>

        <div v-if="isLoading" class="changelog-loading">
          <b-skeleton
            v-for="index in 4"
            :key="index"
            height="56px"
            :animated="true"
          />
        </div>
      </div>

      <footer class="footer-buttons">
        <b-button
          v-if="hasMore"
          :label="$t('change_log_modal.load_more')"
          :loading="isLoading"
          @click="loadMore"
        />
        <b-button :label="$t('shared.close')" @click="onClose" />
      </footer>
    </div>
  </b-modal>
</template>

<script>
import { fieldLabels } from '../ConflictDialog/field-labels';
import {
  GET_JOB_CHANGELOG,
  RESET_JOB_CHANGELOG,
} from '../../store/changeLog/actions/actionTypes';
import {
  GET_CHANGELOG_ENTRIES,
  GET_CHANGELOG_HAS_MORE,
} from '../../store/changeLog/getters/getterTypes';

export default {
  name: 'ChangeLogModal',

  props: {
    jobId: {
      type: String,
      required: true,
    },
    tool: {
      type: String,
      default: '',
    },
    // e.g. /view/editor/work-instructions
    libraryRouteBase: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      isLoading: false,
    };
  },

  computed: {
    entries() {
      return this.$store.getters[GET_CHANGELOG_ENTRIES];
    },

    hasMore() {
      return this.$store.getters[GET_CHANGELOG_HAS_MORE];
    },

    resolvedTool() {
      if (this.tool) {
        return this.tool;
      }
      const pathTool = this.$route?.path?.split('/')[2];
      return this.$route?.meta?.tool || pathTool || 'editor';
    },
  },

  async mounted() {
    await this.fetchPage(true);
  },

  beforeDestroy() {
    this.$store.dispatch(RESET_JOB_CHANGELOG);
  },

  methods: {
    onClose() {
      this.$emit('onClose');
    },

    async fetchPage(reset) {
      this.isLoading = true;
      try {
        await this.$store.dispatch(GET_JOB_CHANGELOG, {
          jobId: this.jobId,
          reset,
        });
      } catch (error) {
        this.$store.dispatch('setErrorMessage', error.message);
      } finally {
        this.isLoading = false;
      }
    },

    async loadMore() {
      await this.fetchPage(false);
    },

    userName(entry) {
      return (
        (entry.user && entry.user.fullname) ||
        this.$t('change_log_modal.unknown_user')
      );
    },

    actionLabel(entry) {
      const key = `change_log_modal.actions.${entry.action}`;
      const label = this.$t(key);
      return label === key ? this.$t('change_log_modal.actions.update') : label;
    },

    entityLabel(entry) {
      const key = `change_log_modal.entities.${entry.entityType}`;
      const label = this.$t(key);
      return label === key ? entry.entityType : label;
    },

    toolLabel(tool) {
      const key = `change_log_modal.tools.${tool}`;
      const label = this.$t(key);
      return label === key ? tool : label;
    },

    fieldLabel(field) {
      if (field === 'question') {
        return this.$t('change_log_modal.entities.question');
      }
      if (field === 'reportNote') {
        return this.$t('change_log_modal.fields.report_note');
      }
      if (field === 'taskComment') {
        return this.$t('change_log_modal.fields.task_comment');
      }
      return fieldLabels[field] || field;
    },

    hasValue(value) {
      return value !== null && value !== undefined && value !== '';
    },

    displayValue(value, field) {
      if (field === 'workStatusCode') {
        const statusKeys = { 0: 'open', 1: 'in_progress', 2: 'signed_off' };
        const statusKey = statusKeys[String(value)];
        if (statusKey) {
          return this.$t(`change_log_modal.status.${statusKey}`);
        }
      }
      if (field === 'jobCompleted') {
        return value
          ? this.$t('change_log_modal.status.completed')
          : this.$t('change_log_modal.status.open');
      }
      if (typeof value !== 'string') {
        return value;
      }
      return value
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    },

    meta(entry) {
      return (entry && entry.meta) || {};
    },

    taskTypeLabel(taskType) {
      const key = `change_log_modal.phrase.task_types.${taskType}`;
      const label = this.$t(key);
      return label === key ? this.$t('change_log_modal.entities.task') : label;
    },

    phraseParts(entry) {
      const action = this.actionLabel(entry);
      const meta = this.meta(entry);
      // Deleted entities can no longer be opened, so don't link them.
      // Bubble/question links target their (still existing) parent task.
      const deletedTarget =
        entry.action === 'delete' &&
        [
          'task',
          'folder',
          'job',
          'repairAssessment',
          'workInstructionPackage',
        ].includes(entry.entityType);
      const href = deletedTarget ? null : this.entityHref(entry);
      const h = this.$t.bind(this);

      if (entry.entityType === 'question' && meta.questionId) {
        const phraseKey = meta.isSubInstruction
          ? 'change_log_modal.phrase.sub_instruction_in_task'
          : 'change_log_modal.phrase.instruction_in_task';
        const taskLabel = this.taskTypeLabel(meta.taskType || 'instruction');
        return [
          { text: `${action} ${h(phraseKey)}` },
          {
            text: meta.taskName ? `${taskLabel} ${meta.taskName}` : taskLabel,
            href,
            emphasis: true,
          },
        ];
      }

      if (
        entry.entityType === 'bubble' &&
        (meta.bubbleName || meta.bubbleNumber || meta.bubbleId)
      ) {
        const parts = [
          { text: action },
          {
            text: `${h('change_log_modal.entities.bubble')} ${
              meta.bubbleName || meta.bubbleNumber || meta.bubbleId
            }`,
            emphasis: true,
          },
        ];
        if (meta.taskName || meta.taskId) {
          parts.push({ text: h('change_log_modal.phrase.on_the') });
          parts.push({
            text: `${this.taskTypeLabel(meta.taskType || 'diagram')} ${
              meta.taskName || meta.taskId
            }`,
            href,
            emphasis: true,
          });
        }
        return parts;
      }

      if (
        entry.entityType === 'task' &&
        (entry.action === 'reorder' || entry.action === 'move')
      ) {
        return [
          {
            text: `${action} ${h('change_log_modal.phrase.tasks_in_project')}`,
          },
        ];
      }

      if (
        (entry.entityType === 'task' || entry.entityType === 'folder') &&
        entry.action === 'copy'
      ) {
        const label =
          meta.taskType && meta.taskType !== 'folder'
            ? this.taskTypeLabel(meta.taskType)
            : this.entityLabel(entry);
        const title = entry.entityName || meta.taskName || '';
        const parts = [
          { text: action },
          { text: `${label} ${title}`.trim(), href, emphasis: true },
        ];
        if (meta.sourceJobName) {
          parts.push({ text: h('change_log_modal.phrase.from') });
          parts.push({ text: meta.sourceJobName, emphasis: true });
        }
        return parts;
      }

      const changedFields = (entry.changes || []).map((change) => change.field);
      if (
        entry.entityType === 'task' &&
        entry.action === 'update' &&
        changedFields.length &&
        changedFields.every(
          (field) => field === 'reportNote' || field === 'taskComment'
        )
      ) {
        const label =
          meta.taskType && meta.taskType !== 'folder'
            ? this.taskTypeLabel(meta.taskType)
            : this.entityLabel(entry);
        const title = entry.entityName || meta.taskName || '';
        return [
          {
            text: `${action} ${h('change_log_modal.phrase.findings_on_the')}`,
          },
          { text: `${label} ${title}`.trim(), href, emphasis: true },
        ];
      }

      if (
        entry.entityType === 'task' &&
        meta.taskType &&
        meta.taskType !== 'folder'
      ) {
        const taskTitle =
          entry.entityName || meta.taskName || meta.taskId || entry.entityId;
        return [
          { text: `${action} ${h('change_log_modal.phrase.the')}` },
          {
            text: `${this.taskTypeLabel(meta.taskType)} ${taskTitle}`,
            href,
            emphasis: true,
          },
        ];
      }

      if (entry.entityType === 'workInstruction') {
        return [
          { text: `${action} ${h('change_log_modal.phrase.wi_in_package')}` },
          {
            text: h('change_log_modal.entities.workInstructionPackage'),
            href,
            emphasis: true,
          },
        ];
      }

      if (entry.entityType === 'job' && entry.action === 'statusChange') {
        return [
          { text: `${action} ${h('change_log_modal.phrase.of')}` },
          { text: this.entityLabel(entry), href, emphasis: true },
        ];
      }

      if (entry.entityType === 'repairAssessment') {
        const canOpenRat = href && this.resolvedTool !== 'report';
        return [
          { text: action },
          {
            text: this.entityLabel(entry),
            href: canOpenRat ? href : null,
            inPlace: Boolean(canOpenRat),
            emphasis: true,
          },
        ];
      }

      return [
        { text: action },
        { text: this.entityLabel(entry), href, emphasis: true },
      ];
    },

    onLinkClick(event, part) {
      event.stopPropagation();
      if (!part.inPlace) {
        return;
      }
      event.preventDefault();
      const query = { ...this.$route.query, openRat: '1' };
      this.$router.replace({ query }).catch(() => null);
      this.onClose();
    },

    headerParts(entry) {
      const meta = this.meta(entry);
      const h = this.$t.bind(this);

      if (
        (entry.action === 'reorder' || entry.action === 'move') &&
        entry.entityType === 'task'
      ) {
        const parts = [];
        if (meta.folderName) {
          parts.push(
            h('change_log_modal.header.project_phase', {
              value: meta.folderName,
            })
          );
        }
        if (meta.movedToFolder || meta.movedToRoot) {
          parts.push(
            h('change_log_modal.header.tasks_moved_to', {
              count: meta.reorderCount || 0,
              target:
                meta.movedToFolder || h('change_log_modal.header.root_level'),
            })
          );
        } else if (meta.reorderCount) {
          parts.push(
            h('change_log_modal.header.tasks_reordered', {
              count: meta.reorderCount,
            })
          );
        } else {
          const firstChange = (entry.changes || [])[0];
          parts.push(
            entry.entityName ||
              (firstChange && firstChange.newValue) ||
              h('change_log_modal.header.tasks_reordered', { count: 0 })
          );
        }
        return parts;
      }

      if (entry.entityType === 'repairAssessment') {
        const parts = [];
        if (meta.isMinor === true || meta.isMinor === false) {
          parts.push(
            meta.isMinor
              ? h('change_log_modal.header.minor')
              : h('change_log_modal.header.major')
          );
        }
        const assembly = meta.assembly || meta.partName;
        if (assembly) {
          parts.push(
            h('change_log_modal.header.assembly', { value: assembly })
          );
        }
        if (meta.rowCount) {
          parts.push(
            h('change_log_modal.header.row_count', { count: meta.rowCount })
          );
        }
        if (!parts.length && entry.entityName) {
          parts.push(entry.entityName);
        }
        return parts;
      }

      if (entry.entityType === 'bubble') {
        const parts = [];
        if (meta.bubbleType) {
          parts.push(
            h('change_log_modal.header.bubble_type', {
              value: meta.bubbleType,
            })
          );
        }
        if (meta.bubbleCount) {
          parts.push(
            h('change_log_modal.header.bubble_count', {
              count: meta.bubbleCount,
            })
          );
        }
        if (!meta.bubbleId && entry.entityName) {
          parts.push(entry.entityName);
        }
        return parts;
      }

      if (entry.entityType === 'question') {
        if (meta.questionCount) {
          return [
            h('change_log_modal.header.question_count', {
              count: meta.questionCount,
            }),
          ];
        }
        const name = meta.questionName || entry.entityName;
        return name ? [h('change_log_modal.header.name', { value: name })] : [];
      }

      if (
        entry.entityType === 'workInstruction' ||
        entry.entityType === 'workInstructionPackage'
      ) {
        return [
          entry.entityName,
          meta.linkedTaskCount !== undefined &&
            h('change_log_modal.header.linked_tasks_count', {
              count: meta.linkedTaskCount,
            }),
          meta.packageId &&
            h('change_log_modal.header.package_id', {
              value: meta.packageId,
            }),
        ].filter(Boolean);
      }

      if (entry.entityType === 'task' || entry.entityType === 'folder') {
        const parts = [];
        if (meta.folderName) {
          parts.push(
            h('change_log_modal.header.project_phase', {
              value: meta.folderName,
            })
          );
        }
        const titleShownInPhrase =
          (meta.taskType &&
            meta.taskType !== 'folder' &&
            entry.action !== 'reorder') ||
          (entry.action === 'copy' && Boolean(entry.entityName));
        if (!titleShownInPhrase) {
          if (entry.entityName) {
            parts.push(entry.entityName);
          } else if (meta.taskName) {
            parts.push(meta.taskName);
          }
        }
        if (meta.taskId && entry.action !== 'create' && !meta.taskType) {
          parts.push(
            h('change_log_modal.header.task_id', { value: meta.taskId })
          );
        }
        return parts;
      }

      if (entry.entityName) {
        return [entry.entityName];
      }
      return [];
    },

    headerText(entry) {
      return this.headerParts(entry).join(' · ');
    },

    visibleChanges(entry) {
      if (entry.action === 'reorder' || entry.action === 'move') {
        return [];
      }
      return (entry.changes || []).filter(
        (change) =>
          change.field !== 'taskOrder' && change.field !== 'copiedFrom'
      );
    },

    entityHref(entry) {
      const { jobId } = this;
      if (!jobId) {
        return null;
      }

      if (jobId.startsWith('library_')) {
        const packageId = jobId.slice('library_'.length);
        if (!this.libraryRouteBase || !packageId) {
          return null;
        }
        return `${this.libraryRouteBase}/${packageId}`;
      }

      const tool = this.resolvedTool;
      if (!tool) {
        return null;
      }

      const meta = this.meta(entry);
      const base = `/view/${tool}/${jobId}`;

      if (entry.entityType === 'repairAssessment') {
        return `${base}?openRat=1`;
      }

      if (entry.entityType === 'folder') {
        const folderRef = meta.taskId || entry.entityId;
        if (!folderRef || folderRef === jobId) {
          return base;
        }
        return `${base}/folder/${folderRef}`;
      }

      if (
        entry.entityType === 'task' ||
        entry.entityType === 'bubble' ||
        entry.entityType === 'question'
      ) {
        const taskRef =
          meta.taskId ||
          meta.taskNumber ||
          (entry.entityType === 'task' ? entry.entityId : null);
        if (!taskRef || taskRef === jobId) {
          return base;
        }
        if (meta.taskType === 'folder') {
          return `${base}/folder/${taskRef}`;
        }
        return `${base}/${taskRef}`;
      }

      if (
        entry.entityType === 'workInstruction' ||
        entry.entityType === 'workInstructionPackage'
      ) {
        if (tool === 'report') {
          return base;
        }
        if (tool === 'editor' || !meta.packageId) {
          return `${base}/wi`;
        }
        return `${base}/wi/${meta.packageId}`;
      }

      if (entry.entityType === 'job') {
        return base;
      }

      return null;
    },

    formatDate(epochMs) {
      if (!epochMs) {
        return '-';
      }
      const date = new Date(epochMs);
      return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })}`;
    },
  },
};
</script>

<style scoped>
.changelog-content {
  background: white;
  border-radius: 10px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 500px;
  max-height: 80vh;
  font-family: 'Siemens Sans', sans-serif;
}

.changelog-header {
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 12px;
}

.changelog-content h2.changelog-title {
  font-size: 18px;
  font-weight: 400;
  margin: 0;
  color: #363636;
}

.changelog-list {
  flex: 1;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 2px;
}

.changelog-entry {
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
}

.changelog-entry:last-child {
  border-bottom: none;
}

.changelog-entry:hover {
  background: #f5f5f5;
}

.entry-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  color: #363636;
  font-size: 14px;
}

.entry-user {
  font-weight: 700;
}

.entry-entity-link {
  color: #0066b3;
  font-weight: 600;
  text-decoration: underline;
}

.entry-entity-link .entry-link-icon {
  vertical-align: text-bottom;
}

.entry-entity-type {
  font-weight: 600;
}

.entry-date {
  margin-left: auto;
  color: #666666;
}

.entry-context {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  font-size: 13px;
  color: #666666;
}

.entry-header-part {
  color: #363636;
}

.entry-tool {
  border: 1px solid #e0e0e0;
  border-radius: 2px;
  padding: 0 6px;
  font-size: 12px;
  text-transform: capitalize;
}

.entry-change {
  display: grid;
  grid-template-columns: 1fr 2fr 2fr;
  gap: 12px;
  align-items: baseline;
  margin-top: 8px;
  font-size: 14px;
  color: #363636;
  overflow-wrap: anywhere;
}

.change-field {
  font-weight: 700;
}

.changelog-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 300px;
  color: #666666;
}

.changelog-loading {
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.footer-buttons {
  display: flex;
  justify-content: end;
  gap: 8px;
}

@media (max-width: 768px) {
  .entry-change {
    grid-template-columns: 1fr;
    gap: 2px;
  }
}
</style>
